import { TourAnchor } from "training/anchors";
import {
  all,
  eligibilityEnabled,
  hasSelfPayPayer,
  not,
  offersPatientEstimate,
  orderIsNoBalanceDue,
  paymentOptionsShown,
  usesPatientBalanceVariant,
} from "training/engine/conditions";
import { ITourStep } from "training/types";

/**
 * CLE-3015 Module 2: Creating and sending an estimate. Text is the curriculum's (curriculum-v2, 2026-10-21),
 * verbatim, except the steps marked "Not curriculum text": branch cards the curriculum describes but gives
 * no trainee wording for (the Patient Balance variant, the eligibility-failure explanation, no payment
 * plans, the $0 / No Balance Due ending). Those are written here and need support's review.
 *
 * Rows split at a sentence boundary so each element gets its own highlight: 2.7 (Member ID, then Run
 * Eligibility) and 2.15 (the footer, then Create and Send).
 */
export const MODULE_2: ITourStep[] = [
  {
    id: "m2.00-before-you-start",
    module: 2,
    mode: "card",
    route: "estimates",
    text: "You are about to create a real estimate. Use your own name, your own cell number and your own email, so the text comes to you. For insurance, use your real plan if you are comfortable, or any made-up member ID; either way we run the check for real so you see what happens.",
  },
  {
    id: "m2.01-new-estimate",
    module: 2,
    anchor: TourAnchor.NewEstimate,
    label: "New Estimate",
    mode: "click",
    // The button navigates to /orders/new itself; the engine only hands the tour over.
    advanceOn: "route",
    route: "estimates",
    text: "Every estimate starts here.",
  },
  {
    id: "m2.02-order-type",
    module: 2,
    anchor: TourAnchor.OrderType,
    label: "Order Type",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    text: "Patient Estimate is the one you will use most: we check the patient's insurance and work out what they owe. Patient Balance is for a known amount, with no insurance check. Clear Package is a cash bundle price. You only see the types your hospital uses.",
  },
  {
    // Not curriculum text — the 2.2 branch: no Patient Estimate on this hospital.
    id: "m2.02v-order-type-patient-balance",
    module: 2,
    anchor: TourAnchor.OrderType,
    label: "Order Type",
    mode: "fill",
    route: "create",
    when: usesPatientBalanceVariant,
    text: 'Pick "Patient Balance". Your hospital does not use Patient Estimate, so this estimate is for a known amount the patient owes, with no insurance check. You only see the types your hospital uses.',
  },
  {
    id: "m2.03-patient-details",
    module: 2,
    anchor: TourAnchor.PatientDetails,
    label: "First name",
    mode: "fill",
    route: "create",
    text: "Type your own details. The phone number is what we text, so get it right; we check it is a real number when you leave the field. Email is optional, and if you add one the patient gets an email too.",
  },
  {
    id: "m2.04-visit",
    module: 2,
    anchor: TourAnchor.VisitCard,
    label: "Visit",
    mode: "fill",
    route: "create",
    text: 'Put the date of service about a week out. Account / Visit ID is the patient\'s account number in your own system; it labels the payment on your Tuesday report. For this test, type TEST. "Not scheduled" sends the estimate without a date.',
  },
  {
    id: "m2.04w-all-changes-saved",
    module: 2,
    anchor: TourAnchor.SaveStatus,
    mode: "look",
    route: "create",
    text: 'The header says "All changes saved" as you type, but nothing is saved until you click Save Draft or Create and Send. Back or Cancel loses the page.',
  },
  {
    // Second run only: the trainee is their own patient, so a second estimate looks like a duplicate.
    id: "m2.04d-duplicate-warning",
    module: 2,
    anchor: TourAnchor.DuplicateWarning,
    mode: "look",
    route: "create",
    optional: true,
    waitMs: 1000,
    text: "The second time you make an estimate for the same patient, a warning says a possible duplicate was found. It will appear for a trainee who runs the tour twice.",
  },
  {
    id: "m2.05-payer",
    module: 2,
    anchor: TourAnchor.PayerName,
    label: "Payer Name",
    mode: "fill",
    route: "create",
    when: all(offersPatientEstimate, hasSelfPayPayer),
    text: 'The list is pre-set to the first payer in your hospital\'s list, so always pick the right one. "Self-Pay Rate" is the choice for a patient with no insurance; it skips the eligibility check and uses your self-pay pricing.',
  },
  {
    id: "m2.05n-payer-no-self-pay",
    module: 2,
    anchor: TourAnchor.PayerName,
    label: "Payer Name",
    mode: "fill",
    route: "create",
    when: all(offersPatientEstimate, not(hasSelfPayPayer)),
    text: "The list is pre-set to the first payer in your hospital's list, so always pick the right one.",
  },
  {
    id: "m2.06-service-type",
    module: 2,
    anchor: TourAnchor.ServiceType,
    label: "Service Type",
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    text: "This tells the payer where the service happens, and it changes the benefits you get back. An ASC picks an ASC Facility option, never a Hospital Outpatient one. Picking the wrong one is the most common mistake we see. Your hospital's usual choice is already selected.",
  },
  {
    id: "m2.07-member-id",
    module: 2,
    anchor: TourAnchor.MemberId,
    label: "Member ID",
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    text: "Type the member ID from the card and run it.",
  },
  {
    id: "m2.07b-run-eligibility",
    module: 2,
    anchor: TourAnchor.RunEligibility,
    label: "Run Eligibility",
    mode: "click",
    route: "create",
    when: eligibilityEnabled,
    text: "It usually takes a few seconds.",
  },
  {
    id: "m2.08-benefits",
    module: 2,
    anchor: TourAnchor.BenefitFields,
    label: "Remaining Deductible",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    text: "This is where the patient stands with their plan today. The tabs on the right show each site of service the payer sent back; click a value there to use it. If the check failed, type a Remaining Deductible of 500 and a Coinsurance Percent of 20 so the estimate has a balance.",
  },
  {
    // Not curriculum text — the 2.7 branch: the check came back with a message instead of benefits.
    // The anchor is on the benefits area only while a message shows, so on success this step is skipped.
    id: "m2.08f-eligibility-message",
    module: 2,
    anchor: TourAnchor.EligibilityMessage,
    mode: "fill",
    route: "create",
    when: eligibilityEnabled,
    optional: true,
    waitMs: 1500,
    text: 'The red message is what came back from the payer. "No eligibility response found", an "Eligibility Verification Error", coverage that shows inactive, or only out-of-network benefits all mean the check did not give us benefits to use. A made-up member ID lands here, and that is fine for this test: type a Remaining Deductible of 500 and a Coinsurance Percent of 20 so the estimate has a balance, then continue.',
  },
  {
    // Second run only.
    id: "m2.08p-previous-orders",
    module: 2,
    anchor: TourAnchor.PreviousOrders,
    mode: "look",
    route: "create",
    when: offersPatientEstimate,
    optional: true,
    waitMs: 1000,
    text: 'The second time you make an estimate for the same patient, the benefits card offers "Apply Reductions" to subtract what an earlier estimate already applied to the deductible.',
  },
  {
    id: "m2.09-add-secondary-insurance",
    module: 2,
    anchor: TourAnchor.AddSecondaryInsurance,
    label: "Add Secondary Insurance",
    mode: "look",
    route: "create",
    when: offersPatientEstimate,
    // Hidden when the payer is Self-Pay Rate; no need to wait the default 8 s for it.
    waitMs: 2000,
    text: "If the patient has a second plan, add it here and run eligibility again.",
  },
  {
    id: "m2.10-cpt-search",
    module: 2,
    anchor: TourAnchor.CptSearch,
    label: "Add CPT services",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    text: "Type a code or a word from the service name, such as MRI, and pick it. Add a second one if you like.",
  },
  {
    id: "m2.11-service-row",
    module: 2,
    anchor: TourAnchor.ServicesTable,
    label: "Description",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    // The table renders once a code is added and priced.
    waitMs: 15000,
    text: 'The price comes from your hospital\'s contract with this payer. A red mark on Amount means the price needs review. Units can be changed, for example for a drug code. "Multiple providers" means a bundle of facility and professional fees; expand it to see each.',
  },
  {
    // Only on hospitals with modality rules, and only when a rule fires.
    id: "m2.11n-service-type-changed",
    module: 2,
    anchor: TourAnchor.ServiceTypeChanged,
    mode: "look",
    route: "create",
    when: eligibilityEnabled,
    optional: true,
    waitMs: 1500,
    text: 'On some hospitals a notice titled "Service Type changed" appears after adding an MRI, CT, X-ray or ultrasound code, because a rule switched the service type to match. A "Keep ..." button puts it back.',
  },
  {
    // Not curriculum text — the Patient Balance variant's "a known balance".
    id: "m2.11v-patient-balance",
    module: 2,
    anchor: TourAnchor.PatientBalanceAmount,
    label: "Patient Balance",
    mode: "fill",
    route: "create",
    when: usesPatientBalanceVariant,
    text: 'Type the amount the patient owes in "Patient Balance". There is no insurance check on this type, so the amount you type is what the patient is asked to pay. For this test, use any amount above $0.',
  },
  {
    id: "m2.12-estimate-results",
    module: 2,
    anchor: TourAnchor.EstimateResults,
    label: "Estimate Results",
    mode: "fill",
    route: "create",
    when: offersPatientEstimate,
    text: "Patient Responsibility is what the patient owes. Contracted Rate is what the payer allows, Insurance Pays is their share. Open the breakdown to see how deductible, coinsurance and copay add up. This is the math the patient sees too. For this test it must be more than $0; if it shows $0, raise the Remaining Deductible.",
  },
  {
    id: "m2.13-payment-options",
    module: 2,
    anchor: TourAnchor.PaymentOptions,
    label: "Offer a payment plan",
    mode: "fill",
    route: "create",
    when: paymentOptionsShown,
    text: 'Plans are on by default if your hospital offers them. Choose "Set number of payments" or "Set amount", the down payment, and monthly or weekly. Leave it on for this test so you can see it on your phone.',
  },
  {
    // Not curriculum text — the 2.13 branch: "Say plans are not set up for this hospital."
    id: "m2.13x-no-payment-plans",
    module: 2,
    mode: "card",
    route: "create",
    when: not(paymentOptionsShown),
    text: "Payment plans are not set up for your hospital, so there is no Payment Options card. The patient pays the full amount at checkout.",
  },
  {
    id: "m2.14-patient-communication",
    module: 2,
    anchor: TourAnchor.PatientCommunication,
    label: "Patient Communication",
    mode: "fill",
    route: "create",
    text: 'This is the text the patient gets. Switch it to Disabled only if the patient asked not to be contacted; then nothing is sent and you collect in person. "Edit message" lets you change the wording for this one patient.',
  },
  {
    id: "m2.15-footer",
    module: 2,
    anchor: TourAnchor.SubmitFooter,
    label: "Save Draft",
    mode: "look",
    route: "create",
    text: "Save Draft keeps it for later under Action Needed and sends nothing. Schedule send picks a date and time for the first text, for example if you build estimates at 6 AM. Create and Send sends it now.",
  },
  {
    id: "m2.15b-create-and-send",
    module: 2,
    anchor: TourAnchor.CreateAndSend,
    label: "Create and Send",
    mode: "click",
    // A click alone is not enough: the form may be invalid or the request may fail. The tour moves on
    // when the create page reports the estimate was created (and its id and status).
    advanceOn: "event",
    event: "order-created",
    route: "create",
    text: "Click Create and Send.",
  },
  {
    // A card, not the toast: the "Estimate Created!" toast lives about 3 seconds, outside any highlight,
    // and is gone before the hospital view has mounted and the tour has found it.
    id: "m2.16-estimate-created",
    module: 2,
    mode: "card",
    route: "estimates",
    text: "Done. Your phone should buzz within a minute. You are back on the Estimates tab, and your estimate is the top row with status Sent.",
  },
  {
    id: "m2.17-your-phone",
    module: 2,
    mode: "card",
    route: "estimates",
    continueLabel: "I've opened it, continue",
    text: 'Open the text and tap the link. Enter your date of birth; that is the check every patient passes. Now you see what the patient sees: what they are having done, what they owe and why, and how they can pay. If you see "Questions?", that is our chat assistant for patients. Do not pay. Come back here.',
  },
  {
    // Not curriculum text — the "2.12 and 2.16 Estimate at $0" branch. Later modules read
    // `trainingProgress.noBalanceDue` to skip 4.1–4.9 and 5.1.
    id: "m2.18-no-balance-due",
    module: 2,
    mode: "card",
    route: "estimates",
    when: orderIsNoBalanceDue,
    text: 'Your estimate came out at $0, so it was created as No Balance Due instead of Sent. Your phone got the "not expected to owe anything" text instead of a payment link. That is what a patient whose deductible is met, or who is on Medicaid, receives. There is nothing to collect on it, so the tour skips the payment steps that follow.',
  },
];
